import { Component, ElementRef, Inject, OnInit, ViewChild } from '@angular/core';
import {
  FormArray,
  UntypedFormArray,
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup,
  ValidationErrors,
  Validators,
} from '@angular/forms';
import {
  MatDialogRef,
  MAT_DIALOG_DATA,
} from '@angular/material/dialog';
import { NavigationEnd, Router } from '@angular/router';
import { select, Store } from '@ngrx/store';
import { filter, Observable, Subscription, take } from 'rxjs';
import { Patterns, SmoothScrollingService } from '@hrra/core';
import { ButtonTypeAttributeEnum, ButtonTypeEnum } from '@hrra/ui';
import { IconButtonTypeEnum } from '@hrra/ui';
import {
  changeWorkExperienceIsCurrentForAdmin,
  downloadWorkExperienceAttachmentForAdmin,
  editJobseekerWorkExperienceForAdmin,
  setJobseekerWorkExperienceForAdmin,
  setSelectedCategoriesForAdmin,
  setSelectedIndustriesForAdmin,
  updateEmploymentFormTypeForAdmin,
  updateSeniorityLevelIdForAdmin,
  setSelectedSpecializationsForAdmin,
  removeLocation,
  addLocation,
  workExperienceFormSubmit,
  updateWorkScheduleIdForAdmin,
  updateEmploymentFormNewIdForAdmin,
} from 'src/app/root-store/jobseeker-store/jobseeeker-profile-for-admin/actions';
import {
  isFileUploading,
  selectedIndustries,
  selectedSpecializations,
  selectFileName,
  selectIsFileSizeValid,
  selectIsJobseekerProfileUpdating,
  selectJobseekerId,
  selectJobseekerModerationLastChangeDate,
  selectJobseekerWorkExperience,
  selectMonthList,
  selectSelectedCategoryIds,
  selectSelectedSpecializationIds,
  selectSelectedIndustryIds,
  selectSelectedSeniorityLevel,
  selectSelectedFileText,
  selectWorkExperienceIsCurrent,
  selectIsEnableExtendedLocationAddButton,
  selectIsWorkExperienceFormSubmitted,
  selectExtendedLocationCountLoading,
  selectSelectedEmploymentFormNewForAdmin,
  selectSelectedWorkScheduleForAdmin,
  selectWorkExperienceHasAttachmentForAdmin,
} from 'src/app/root-store/jobseeker-store/jobseeeker-profile-for-admin/selectors';
import {
  selectEmploymentFormOptions,
  selectEmploymentNewFormsOptions,
  selectEmploymentTypeOptions,
  selectLocationFirstLevelId,
  selectLocationLastHiddenLevelCount,
  selectLocationLevelCount,
  selectLocationRequiredLevelCount,
  selectSeniorityLevelOptions,
  selectSystemConfigs,
  selectWorkScheduleOptions,
} from 'src/app/root-store/selectors';
import { AppState } from 'src/app/root-store/state';
import { SystemConfig } from 'src/app/shared/models/system-config-model';
import { SpecializationPickerService } from '../shared/services/specialization-picker.service';
import { JobseekerFileService } from '../shared/services/jobseeker-file.service';
import {
  FileUploadResultViewModel,
  JobseekerWorkExperienceModel,
} from '../shared/services/jobseeker.service';
import {
  JobseekerWorkExperienceViewModel,
  BasicOptionItem,
  EmploymentFormTypeEnum,
} from '@hrra/common-models';
import { IndustryPickerService } from '../shared/services/industry-picker.service';
import { SeniorityLevelItem } from 'src/app/shared/models/seniority-level.model';
import { EmploymentFormTypeItem } from 'src/app/shared/models/employment-forms';
import { JobseekerExtendedLocationModel } from 'src/app/shared/models/jobseeker-location.model';
import { ExtendedLocationPath } from 'src/app/shared/models/location-picker-view-model';
import { WorkScheduleItem } from 'src/app/shared/models/work-shchedule.model';
import { EmploymentContractTypeItem } from 'src/app/shared/models/employment-contract.model';

@Component({
    selector: 'app-jobseeker-work-experience-edit-for-admin',
    templateUrl: './redesign/jobseeker-work-experience-edit-for-admin.component.html',
    styleUrls: ['./redesign/jobseeker-work-experience-edit-for-admin.component.scss'],
    standalone: false
})
export class JobseekerWorkExperienceEditForAdminComponent implements OnInit {
  navigationSubs: Subscription;
  isCurrentSubs: Subscription;

  editForm: UntypedFormGroup;

  @ViewChild('scrollContainer') scrollContainer: ElementRef;

  jobseekerWorkExperience$: Observable<JobseekerWorkExperienceViewModel>;
  monthList$: Observable<BasicOptionItem[]>;
  selectedFile$: Observable<FileUploadResultViewModel>;
  isFileUploading$: Observable<boolean>;
  selectedCategoryIds$: Observable<number[]>;
  selectedCategories$: Observable<any>;
  isCurrent$: Observable<boolean>;
  isUpdating$: Observable<boolean>;
  selectedFileText$: Observable<string>;
  isFileSizeValid$: Observable<boolean>;
  systemConfigs$: Observable<SystemConfig>;
  employmentFormTypeEnum: any;
  selectHasAttachment$: Observable<boolean>;

  public workScheduleItems$: Observable<WorkScheduleItem[]>;
  public employmentFormTypeItems$: Observable<EmploymentContractTypeItem[]>;
  public selectedWorkSchedule$: Observable<string>;
  public selectedEmploymentFormNew$: Observable<string>;

  jobseekerId$: Observable<number>;
  moderationLastChangeDate$: Observable<Date>;

  public selectedIndustryIds$: Observable<string[]>;
  public selectIndustries$: Observable<
    { head: string; subindustries: string[] }[]
  >;

  public selectedSpecializationIds$: Observable<string[]>;
  public selectedSpecializations$: Observable<any>;

  public isFormSubmitted$: Observable<boolean>;

  public extendedLocationCount: Subscription;

  public seniorityLevelItems$: Observable<SeniorityLevelItem[]>;
  public selectedSeniorityLevelId$: Observable<string>;

  public employmentFormTypes$: Observable<EmploymentFormTypeItem[]>;

  public title: string;
  public buttonType: any;
  public buttonAttributeType: any;
  public iconButtonType: any;
  public categoryButtonIsTouched: boolean;

  public locationLevelCount$: Observable<number>;
  public locationRequiredLevelCount$: Observable<number>;
  public locationLastHiddenLevelCount$: Observable<number>;
  public locationFirstLevelId$: Observable<string>;

  public industryButtonIsTouched: boolean;

  public isAddLocationEnabled$: Observable<boolean>;

  public specializationButtonIsTouched: boolean;

  constructor(
    private fb: UntypedFormBuilder,
    private store: Store<AppState>,
    @Inject(MAT_DIALOG_DATA) public data,
    private specializationPickerService: SpecializationPickerService,
    public dialogRef: MatDialogRef<JobseekerWorkExperienceEditForAdminComponent>,
    private fileService: JobseekerFileService,
    private industryPickerService: IndustryPickerService,
    private router: Router,
    private smoothScrollService: SmoothScrollingService
  ) {
    this.iconButtonType = IconButtonTypeEnum;
    this.buttonType = ButtonTypeEnum;
    this.buttonAttributeType = ButtonTypeAttributeEnum;
    this.employmentFormTypeEnum = EmploymentFormTypeEnum

    this.editForm = this.fb.group(
      {
        positionTitle: new UntypedFormControl(null, [Validators.required]),
        employer: new UntypedFormControl(null, [Validators.required]),
        dateFromMonth: new UntypedFormControl(null),
        dateFromYear: new UntypedFormControl(null, [
          Validators.required,
          Validators.pattern(Patterns.year),
        ]),
        dateToMonth: new UntypedFormControl(null),
        dateToYear: new UntypedFormControl(null, [
          Validators.pattern(Patterns.year),
        ]),
        isCurrent: new UntypedFormControl(false),
        workScheduleId: new UntypedFormControl(null),
        employmentFormId: new UntypedFormControl(null),
        description: new UntypedFormControl(null),
        extendedLocationPathList: new UntypedFormArray(
          [new UntypedFormControl(null)],
          [Validators.required]
        ),
        employmentFormTypeId: new UntypedFormControl(EmploymentFormTypeEnum.OnSite),
      },
      { validator: [this.customValidator, this.extendedLocationValidator, this.extendedLocationsRequired] }
    );

    this.navigationSubs = this.router.events.subscribe((e) => {
      if (e instanceof NavigationEnd) {
        this.dialogRef.close();
      }
    });
  }

  customValidator(group: UntypedFormGroup): { [s: string]: boolean } {
    if (group) {
      let dateToYear =
        group.controls['dateToYear'].value == null ||
        group.controls['dateToYear'].value == ''
          ? null
          : +group.controls['dateToYear'].value;
      let dateToMonth = group.controls['dateToMonth'].value;
      let dateFromYear = +group.controls['dateFromYear'].value;
      let dateFromMonth = group.controls['dateFromMonth'].value;
      let isCurrent = group.controls['isCurrent'].value;

      dateFromMonth = dateFromMonth || 1;
      dateToMonth = dateToMonth || 12;

      var isDateValid =
        isCurrent ||
        dateToYear == null ||
        dateFromYear < dateToYear ||
        (dateToYear == dateFromYear && dateFromMonth <= dateToMonth);

      let customValidations: { [s: string]: boolean } = {};

      if (!isDateValid) {
        customValidations['date'] = true;
      }

      if (!isCurrent && dateToYear == null) {
        customValidations['RequiredYear'] = true;
      }

      return customValidations;
    }
  }

  extendedLocationValidator(group: UntypedFormGroup): ValidationErrors | null {
    if (group) {
      const extendendLocations = group.controls['extendedLocationPathList'].value;
      const extendedLocationSet = new Set<string>();
      const employmentFormType = group.controls['employmentFormTypeId'].value

      for (let i = 0; i < extendendLocations.length; i++) {
        if (extendendLocations[i] == null) continue;
        const locationKey = extendendLocations[i].locations
          .map((c) => c.locationId)
          .join(',');

        if (extendedLocationSet.has(locationKey) && employmentFormType !== EmploymentFormTypeEnum.WorkFromHome) {
          return { SameExtendedLocation: true };
        }

        extendedLocationSet.add(locationKey);
      }
    }
    return null;
  }

  extendedLocationsRequired(group: UntypedFormGroup): ValidationErrors | null {
    if (group) {
      const controls = group.controls['extendedLocationPathList']['controls'];
      let hasValidExtendedLocation = false;
      const employmentFormType = group.controls['employmentFormTypeId'].value


      for (let i = 0; i < controls.length; i++) {
        const control = controls[i];
        if (
          (control.touched || control.value != null) &&
          control.errors == null && employmentFormType !== EmploymentFormTypeEnum.WorkFromHome
        ) {
          hasValidExtendedLocation = true;
          break;
        }
      }

      if (!hasValidExtendedLocation && employmentFormType !== EmploymentFormTypeEnum.WorkFromHome) {
        return { ExtendedLocationsRequired: true };
      }
    }
    return null;
  }

  get locationControl() {
    return this.editForm.get('extendedLocationPathList') as FormArray;
  }

  ngOnInit(): void {
    this.title = this.data.title;

    this.extendedLocationCount = this.store
      .pipe(
        select(selectExtendedLocationCountLoading),
        filter((c) => !c.loading),
        take(1)
      )
      .subscribe((c) => {
        const extendedLocationsArray = this.editForm.get(
          'extendedLocationPathList'
        ) as FormArray;
        this.editForm
          .get('extendedLocationPathList')
          .setValidators(Validators.maxLength(c.maxExtendedLocationCount));
        if (extendedLocationsArray.length > 0) {
          extendedLocationsArray.removeAt(0);
        }
        for (let i = 0; i < c.count; i++) {
          extendedLocationsArray.push(new UntypedFormControl(null));
        }
      });

    this.monthList$ = this.store.pipe(select(selectMonthList));
    this.jobseekerWorkExperience$ = this.store.pipe(
      select(selectJobseekerWorkExperience)
    );
    this.selectedFile$ = this.store.pipe(select(selectFileName));
    this.isFileUploading$ = this.store.pipe(select(isFileUploading));
    this.selectedCategoryIds$ = this.store.pipe(
      select(selectSelectedCategoryIds)
    );
    this.selectIndustries$ = this.store.pipe(select(selectedIndustries));
    this.selectedIndustryIds$ = this.store.pipe(
      select(selectSelectedIndustryIds)
    );
    this.selectedSpecializationIds$ = this.store.pipe(
      select(selectSelectedSpecializationIds)
    );
    this.selectedSpecializations$ = this.store.pipe(
      select(selectedSpecializations)
    );
    this.isCurrent$ = this.store.pipe(select(selectWorkExperienceIsCurrent));
    this.isUpdating$ = this.store.pipe(
      select(selectIsJobseekerProfileUpdating)
    );
    this.selectedFileText$ = this.store.pipe(select(selectSelectedFileText));
    this.isFileSizeValid$ = this.store.pipe(select(selectIsFileSizeValid));
    this.systemConfigs$ = this.store.pipe(select(selectSystemConfigs));
    this.workScheduleItems$ = this.store.pipe(
      select(selectWorkScheduleOptions)
    );
    this.selectHasAttachment$ = this.store.pipe(select(selectWorkExperienceHasAttachmentForAdmin));
    this.employmentFormTypeItems$ = this.store.pipe(
      select(selectEmploymentNewFormsOptions)
    );
    this.selectedWorkSchedule$ = this.store.pipe(
      select(selectSelectedWorkScheduleForAdmin)
    );
    this.selectedEmploymentFormNew$ = this.store.pipe(
      select(selectSelectedEmploymentFormNewForAdmin)
    );

    this.locationLevelCount$ = this.store.pipe(
      select(selectLocationLevelCount)
    );
    this.locationRequiredLevelCount$ = this.store.pipe(
      select(selectLocationRequiredLevelCount)
    );
    this.locationLastHiddenLevelCount$ = this.store.pipe(
      select(selectLocationLastHiddenLevelCount)
    );
    this.locationFirstLevelId$ = this.store.pipe(
      select(selectLocationFirstLevelId)
    );
    this.seniorityLevelItems$ = this.store.pipe(
      select(selectSeniorityLevelOptions)
    );
    this.isAddLocationEnabled$ = this.store.pipe(
      select(selectIsEnableExtendedLocationAddButton)
    );
    this.selectedSeniorityLevelId$ = this.store.pipe(
      select(selectSelectedSeniorityLevel)
    );
    this.employmentFormTypes$ = this.store.pipe(
      select(selectEmploymentTypeOptions)
    );
    this.isFormSubmitted$ = this.store.pipe(
      select(selectIsWorkExperienceFormSubmitted)
    );

    this.jobseekerId$ = this.store.select(selectJobseekerId);
    this.moderationLastChangeDate$ = this.store.select(
      selectJobseekerModerationLastChangeDate
    );

    this.handleEmploymentTypeChange();

    // the end date is disabled while "currently work here" is checked
    this.isCurrentSubs = this.isCurrent$.subscribe(isCurrent => {
      for (const name of ['dateToMonth', 'dateToYear']) {
        const control = this.editForm.get(name);
        isCurrent ? control.disable({ emitEvent: false }) : control.enable({ emitEvent: false });
      }
    });

    setTimeout(() => {
      this.store.dispatch(
        setJobseekerWorkExperienceForAdmin({
          payload: this.data.workExperience,
        })
      );
    }, 0);
  }

  onFileSelect(file: File, fileLimit: number, jobseekerId: number) {
    this.fileService.uploadFileForAdmin(file, fileLimit, jobseekerId);
  }

  handleEmploymentTypeChange(): void {
    this.editForm.get('employmentFormTypeId')?.valueChanges.subscribe((value) => {
      const extendedLocations = this.editForm.get(
        'extendedLocationPathList'
      ) as UntypedFormArray;

      if (value === EmploymentFormTypeEnum.WorkFromHome) {
        extendedLocations.clear();
        extendedLocations.push(
          new UntypedFormControl({ value: null, disabled: true })
        );
        extendedLocations.disable();
      } else {
        if (extendedLocations.disabled) {
          extendedLocations.enable();
        }

        if (extendedLocations.length === 0) {
          extendedLocations.push(new UntypedFormControl(null));
        }
      }
    });
  }

  downloadAttachment(experienceId: string, jobseekerId: number) {
    this.store.dispatch(
      downloadWorkExperienceAttachmentForAdmin({
        payload: { experienceId, jobseekerId },
      })
    );
  }

  onSelectSeniorityLevel(event) {
    this.store.dispatch(
      updateSeniorityLevelIdForAdmin({
        payload: { seniorityLevelId: event.value },
      })
    );
  }

  onSelectWorkSchedule(event) {
    this.store.dispatch(
      updateWorkScheduleIdForAdmin({ payload: { workScheduleId: event.value } })
    );
  }

  onSelectEmploymentFormNew(event) {
    this.store.dispatch(
      updateEmploymentFormNewIdForAdmin({
        payload: { employmentFormNewId: event.value },
      })
    );
  }

  updateEmploymentFormType(employmentTypeId: number) {
    this.store.dispatch(
      updateEmploymentFormTypeForAdmin({
        payload: { employmentTypeId },
      })
    );
  }

  removeLocation(index: number) {
    const extendedLocations = this.editForm.get(
      'extendedLocationPathList'
    ) as FormArray;
    extendedLocations.removeAt(index);
    this.store.dispatch(removeLocation({ payload: { index } }));
  }

  resetLocation(index: number) {
    const extendedLocations = this.editForm.get(
      'extendedLocationPathList'
    ) as FormArray;
    extendedLocations.controls[index].reset();
  }

  addNewLocation() {
    const extendedLocations = this.editForm.get(
      'extendedLocationPathList'
    ) as FormArray;
    extendedLocations.push(new UntypedFormControl(null));
    this.store.dispatch(addLocation());
  }

  onSave(
    filename: string,
    specializations: string[],
    industries: string[],
    seniorityLevel: string,
    workSchedule: string,
    employmentFormNew: string,
    isFileSizeValid: boolean,
    jobseekerId: number,
    moderationLastChangeDate: Date
  ) {

    if(this.editForm.invalid || specializations?.length === 0 || industries?.length === 0 || !isFileSizeValid || !seniorityLevel || !workSchedule || !employmentFormNew){
      this.smoothScrollService.scrollToFirstInvalidControl(this.scrollContainer, 'input.ng-invalid, .has-error')
    }
    
    this.store.dispatch(workExperienceFormSubmit({ payload: true }));

    if (
      this.editForm.valid &&
      specializations?.length > 0 &&
      industries?.length > 0 &&
      isFileSizeValid && seniorityLevel && workSchedule && employmentFormNew
    ) {
      var value = this.editForm.value;

      const extendedLocationsList: JobseekerExtendedLocationModel[] =
        value.extendedLocationPathList?.filter((e) => e != null)
          .map((extendedLocations: ExtendedLocationPath, index: number) => {
            const locations = extendedLocations.locations;
            const firstHiddenLocationIdIndex = locations.findIndex(
              (c) => c.isHidden == true
            );
            let lastLocationIdIndex = locations.length;

            for (let i = locations.length - 1; i >= 0; i--) {
              if (
                locations[i].locationId !== '' &&
                locations[i].locationId !== undefined
              ) {
                lastLocationIdIndex = i;
                break;
              }
            }

            const jobseekerExtendedLocation = <JobseekerExtendedLocationModel>{
              locationId: locations[lastLocationIdIndex].locationId,
              lastVisibleLocationId:
                firstHiddenLocationIdIndex === -1
                  ? null
                  : locations[firstHiddenLocationIdIndex - 1].locationId,
              jobseekerId: 0,
              jobseekerExtendedLocationId: 0,
            };

            return jobseekerExtendedLocation;
          });

      let jobseekerWorkexperience = <JobseekerWorkExperienceModel>{
        jobseekerWorkExperienceId: this.data.workExperience?.experienceId,
        experienceId: this.data.workExperience?.experienceId,
        dateFromMonth: value.dateFromMonth,
        dateFromYear: value.dateFromYear,
        // disabled while the jobseeker still works here, so missing from the form value
        dateToMonth: value.dateToMonth ?? null,
        dateToYear: value.dateToYear ?? null,
        isCurrent: value.isCurrent,
        employer: value.employer,
        positionTitle: value.positionTitle,
        description: value.description,
        specializations: specializations,
        industries: industries,
        extendedLocations: extendedLocationsList,
        seniorityLevelId: seniorityLevel,
        workScheduleId: workSchedule,
        employmentFormId: employmentFormNew,
        fileName: filename,
        employmentTypeId: value.employmentFormTypeId,
      };

      this.store.dispatch(
        editJobseekerWorkExperienceForAdmin({
          payload: {
            jobseekerWorkexperience,
            jobseekerId,
            moderationLastChangeDate,
          },
        })
      );
    }
  }

  onCurrentExperience(isCurrent) {
    if (!isCurrent) {
      this.editForm.get('dateToMonth').reset();
      this.editForm.get('dateToYear').reset();
    }
    this.store.dispatch(changeWorkExperienceIsCurrentForAdmin());
  }

  public specializationPicker(specializations: string[]) {
    this.specializationButtonIsTouched = true;
    this.specializationPickerService.pickSpecialization((specializationIds) => {
      this.store.dispatch(
        setSelectedSpecializationsForAdmin({ payload: specializationIds })
      );
    }, specializations);
  }

  public industryPicker(industries: string[]) {
    this.industryButtonIsTouched = true;
    this.industryPickerService.pickIndustry((industryIds) => {
      this.store.dispatch(
        setSelectedIndustriesForAdmin({ payload: industryIds })
      );
    }, industries);
  }

  ngOnDestroy() {
    this.navigationSubs.unsubscribe();
    this.extendedLocationCount.unsubscribe();
    this.isCurrentSubs.unsubscribe();
  }
}
